import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    IconButton,
    MenuItem,
    Select, Stack,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
} from "@mui/material";
import {format} from "date-fns";
import {useEffect, useMemo, useState} from "react";
import {helper} from "../../../../utils/helper.js";
import moment from "moment-timezone";
import Button from "@mui/material/Button";
import Constants from "../../../../utils/constants.js";
import {connect, useDispatch} from "react-redux";
import {heuresActions} from "../../../../modules/heures/heures.actions.js";
import CardTravelIcon from "@mui/icons-material/CardTravel";
import OperateurTourneeStats from "./OperateurTourneeStats";
import EquipageDetailHeader from "./EquipageDetailHeader.js";
import EquipageTimesheetTable from "./EquipageTimesheetTable.js";
import EquipageAlerteRHCard from "./EquipageAlerteRHCard.js";
import EquipageVehicleTimeline from "./EquipageVehicleTimeline.js";

function EquipageDetailModal({
                                 posteOperateurId,
                                 onFdtUpdate,
                                 onFdtValidate,
                                 open,
                                 handleClose,
                                 data,
                                 onRefresh,
                                 initialStatutJournee,
                                 onStatutJourneeChange,
                                 ...props
                             }) {
    const dispatch = useDispatch();

    useEffect(() => {
        if (open && posteOperateurId) {
            dispatch(heuresActions.fetchEquipageDetail(posteOperateurId));
            setIsLoading(false);
        }
    }, [open, posteOperateurId, dispatch]);

    const [isLoading, setIsLoading] = useState(false);

    const [isEditingStart, setIsEditingStart] = useState(false);
    const [isEditingEnd, setIsEditingEnd] = useState(false);
    const [isEditingPause, setIsEditingPause] = useState(false);
    const [heureDebutValue, setHeureDebutValue] = useState(null);
    const [heureFinValue, setHeureFinValue] = useState(null);
    const [pauseValue, setPauseValue] = useState(null);

    const [isEditingStartBinome, setIsEditingStartBinome] = useState(false);
    const [isEditingEndBinome, setIsEditingEndBinome] = useState(false);
    const [isEditingPauseBinome, setIsEditingPauseBinome] = useState(false);
    const [heureDebutBinome, setHeureDebutBinome] = useState(null);
    const [heureFinBinome, setHeureFinBinome] = useState(null);
    const [pauseBinome, setPauseBinome] = useState(null);
    const [statutJournee, setStatutJournee] = useState(initialStatutJournee || "WD");
    const [isUpdatingPanier, setIsUpdatingPanier] = useState(false);

    const pauseData = Constants.TEMPS_PAUSE_MINUTES_DATA;

    const hasDifference = useMemo(() => {
        if (!data?.binome) {
            return { debut: false, fin: false, pause: false };
        }

        const areDatesEqual = (date1, date2) => {
            if (!date1 || !date2) return true;
            const format1 = helper.getFormattedTime(date1);
            const format2 = helper.getFormattedTime(date2);
            return format1 === format2;
        };

        const debutDiff = heureDebutValue && heureDebutBinome &&
            !areDatesEqual(heureDebutValue, heureDebutBinome);
        const finDiff = heureFinValue && heureFinBinome &&
            !areDatesEqual(heureFinValue, heureFinBinome);
        const pauseDiff = pauseValue != null && pauseBinome != null &&
            pauseValue !== pauseBinome;

        return { debut: debutDiff, fin: finDiff, pause: pauseDiff };
    }, [data?.binome, heureDebutValue, heureFinValue, pauseValue, heureDebutBinome, heureFinBinome, pauseBinome]);

    useEffect(() => {
        if (data?.feuilleDeTemps) {
            setHeureDebutValue(helper.parseTimeWithoutTimezone(data.feuilleDeTemps.heureDebut));
            setHeureFinValue(helper.parseTimeWithoutTimezone(data.feuilleDeTemps.heureFin));
            setPauseValue(data.feuilleDeTemps.tempsPauseMinutes ?? 0);
            setStatutJournee(initialStatutJournee || data.statutJournee || "WD");
        }
        if (data?.binome) {
            if (data.binome.feuilleDeTemps) {
                setHeureDebutBinome(helper.parseTimeWithoutTimezone(data.binome.feuilleDeTemps.heureDebut));
                setHeureFinBinome(helper.parseTimeWithoutTimezone(data.binome.feuilleDeTemps.heureFin));
                setPauseBinome(data.binome.feuilleDeTemps.tempsPauseMinutes || 0);
            } else {
                setHeureDebutBinome(null);
                setHeureFinBinome(null);
                setPauseBinome(0);
            }
        }
    }, [data, initialStatutJournee]);

    const engineTimesDebut = data?.laveusePrisePoste?.engineOn || null;
    const engineTimesFin = data?.laveuseFinPoste?.engineOff || null;
    const binomeEngineTimesDebut = data?.binome?.laveusePrisePoste?.engineOn || null;
    const binomeEngineTimesFin = data?.binome?.laveuseFinPoste?.engineOff || null;

    const renderLaveuseTimes = (laveuseDebut, laveuseFin, engineOn, engineOff, isStart = true) => {
        const lignes = [];
        if (laveuseDebut) {
            lignes.push(
                <Typography key="debut" variant="body2">
                    {laveuseDebut.description} -
                    {isStart ? (engineOn && ` : ${engineOn}`) : (engineOff && ` : ${engineOff}`)}
                </Typography>
            );
        }
        if (laveuseFin && (!laveuseDebut || laveuseFin.id !== laveuseDebut.id)) {
            lignes.push(
                <Typography key="fin" variant="body2">
                    {laveuseFin.description} ({laveuseFin.immatriculation})
                    {isStart ? (engineOn && ` : ${engineOn}`) : (engineOff && ` : ${engineOff}`)}
                </Typography>
            );
        }
        return lignes.length > 0 ? lignes : "-";
    };

    const getTempsTravail = (workMinutes) => {
        const totalMinutes = workMinutes || 0;
        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        return `${hours}h${minutes > 0 ? minutes + "m" : ""}`;
    };

    const handleStatutJourneeChange = (event, newValue) => {
        if (newValue !== null) {
            setStatutJournee(newValue);
            if (onStatutJourneeChange && typeof onStatutJourneeChange === 'function') {
                onStatutJourneeChange(newValue);
            }
            if (posteOperateurId) {
                setIsLoading(true);
                dispatch(heuresActions.updatePosteOperateur(posteOperateurId, { statutJournee: newValue }))
                    .then(() => {
                        if (onRefresh && typeof onRefresh === 'function') onRefresh();
                        if (posteOperateurId) dispatch(heuresActions.fetchEquipageDetail(posteOperateurId));
                        setIsLoading(false);
                    })
                    .catch(() => setIsLoading(false));
            }
        }
    };

    const handlePanierRepasToggle = (checked, targetPosteOperateurId) => {
        if (!targetPosteOperateurId) return;
        setIsUpdatingPanier(true);
        dispatch(heuresActions.updatePosteOperateur(targetPosteOperateurId, { panierRepas: checked }))
            .then(() => {
                if (onRefresh && typeof onRefresh === 'function') onRefresh();
                if (posteOperateurId) dispatch(heuresActions.fetchEquipageDetail(posteOperateurId));
            })
            .finally(() => setIsUpdatingPanier(false));
    };

    const handleCreateBinomeFdt = () => {
        if (data?.binome?.id && data?.date && heureDebutValue && heureFinValue) {
            setIsLoading(true);
            dispatch(heuresActions.createEmptyFeuilleDeTemps(
                data.binome.id,
                data.date,
                statutJournee,
                format(heureDebutValue, 'yyyy-MM-dd HH:mm:ss'),
                format(heureFinValue, 'yyyy-MM-dd HH:mm:ss'),
                pauseValue || 0
            ))
            .then(() => {
                if (onRefresh && typeof onRefresh === 'function') onRefresh();
                if (posteOperateurId) dispatch(heuresActions.fetchEquipageDetail(posteOperateurId));
                setIsLoading(false);
            })
            .catch(() => setIsLoading(false));
        }
    };

    const handleEditClick = (type, isBinome = false) => {
        if (type === "start") {
            isBinome ? setIsEditingStartBinome(true) : setIsEditingStart(true);
        } else if (type === "end") {
            isBinome ? setIsEditingEndBinome(true) : setIsEditingEnd(true);
        } else if (type === "pause") {
            isBinome ? setIsEditingPauseBinome(true) : setIsEditingPause(true);
        }
    };

    const handleCancel = (type, isBinome = false) => {
        if (type === "start") {
            isBinome ? setIsEditingStartBinome(false) : setIsEditingStart(false);
            isBinome
              ? setHeureDebutBinome(helper.parseTimeWithoutTimezone(data?.binome?.feuilleDeTemps?.heureDebut))
              : setHeureDebutValue(helper.parseTimeWithoutTimezone(data?.feuilleDeTemps?.heureDebut));
        } else if (type === "end") {
            isBinome ? setIsEditingEndBinome(false) : setIsEditingEnd(false);
            isBinome
              ? setHeureFinBinome(helper.parseTimeWithoutTimezone(data?.binome?.feuilleDeTemps?.heureFin))
              : setHeureFinValue(helper.parseTimeWithoutTimezone(data?.feuilleDeTemps?.heureFin));
        } else if (type === "pause") {
            isBinome ? setIsEditingPauseBinome(false) : setIsEditingPause(false);
            isBinome
              ? setPauseBinome(data?.binome?.feuilleDeTemps?.tempsPauseMinutes || 0)
              : setPauseValue(data?.feuilleDeTemps?.tempsPauseMinutes || 0);
        }
    };

    const handleSave = (type, isBinome = false) => {
        const idFdt = isBinome
          ? data?.binome?.feuilleDeTemps?.id
          : data?.feuilleDeTemps?.id;

        let values = {id: idFdt};

        if (type === "start") {
            values.heureDebut = isBinome
              ? helper.formatTimeToServer(heureDebutBinome)
              : helper.formatTimeToServer(heureDebutValue);
            isBinome ? setIsEditingStartBinome(false) : setIsEditingStart(false);
        }
        if (type === "end") {
            values.heureFin = isBinome
              ? helper.formatTimeToServer(heureFinBinome)
              : helper.formatTimeToServer(heureFinValue);
            isBinome ? setIsEditingEndBinome(false) : setIsEditingEnd(false);
        }
        if (type === "pause") {
            values.tempsPauseMinutes = isBinome ? pauseBinome : pauseValue;
            isBinome ? setIsEditingPauseBinome(false) : setIsEditingPause(false);
        }

        if (onFdtUpdate) onFdtUpdate(values);
    };

    const pendingAlertCount = data?.alertes?.filter(a => a.status === 'pending').length ?? 0;
    const pendingCount = pendingAlertCount;

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth={"xl"}
            fullWidth
            PaperProps={{ sx: { borderRadius: "16px", overflow: "hidden" } }}
        >
            {/* Header sombre */}
            {data && (
                <EquipageDetailHeader
                    data={data}
                    onClose={handleClose}
                    pendingAlertCount={pendingAlertCount}
                />
            )}

            <DialogContent sx={{ p: "20px 24px", display: "flex", flexDirection: "column", gap: "20px" }}>
                {props.loading && (
                    <Box display="flex" justifyContent="center" py={4}>
                        <CircularProgress />
                    </Box>
                )}

                {data && (
                    <>
                        {/* Type de journée */}
                        <Box>
                            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                                    Type de journée
                                </Typography>
                                {/* Lecture seule : l'affectation se gère depuis la vue de validation hebdo */}
                                {data.deplacement && (
                                    <Chip
                                        icon={<CardTravelIcon style={{ fontSize: 14 }} />}
                                        label="Déplacement (forfait semaine)"
                                        size="small"
                                        sx={{ background: "#f3e8ff", color: "#6A1B9A", fontWeight: 600 }}
                                    />
                                )}
                            </Stack>
                            <ToggleButtonGroup
                                color="primary"
                                exclusive
                                size="medium"
                                value={statutJournee}
                                onChange={handleStatutJourneeChange}
                                aria-label="statut de la journée"
                                sx={{ flexWrap: 'wrap' }}
                            >
                                {Constants.RH_STATUT_JOURNEE_DATA.map((option) => (
                                    <ToggleButton
                                        key={option.id}
                                        value={option.id}
                                        style={{
                                            backgroundColor: statutJournee === option.id ? option.color : undefined,
                                            color: statutJournee === option.id ? option.fontColor : undefined,
                                        }}
                                        className="toggle-button"
                                    >
                                        {option.label}
                                    </ToggleButton>
                                ))}
                            </ToggleButtonGroup>
                        </Box>

                        {/* Tableau éditable */}
                        <Box>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                                ✏️ Saisie opérateur <span style={{ color: "#3b82f6", fontWeight: 400, textTransform: "none", letterSpacing: 0 }}>— cliquer sur une valeur pour la modifier</span>
                            </Typography>
                            <EquipageTimesheetTable
                                data={data}
                                isEditingStart={isEditingStart}
                                isEditingEnd={isEditingEnd}
                                isEditingPause={isEditingPause}
                                heureDebutValue={heureDebutValue}
                                heureFinValue={heureFinValue}
                                pauseValue={pauseValue}
                                heureDebutBinome={heureDebutBinome}
                                heureFinBinome={heureFinBinome}
                                pauseBinome={pauseBinome}
                                isEditingStartBinome={isEditingStartBinome}
                                isEditingEndBinome={isEditingEndBinome}
                                isEditingPauseBinome={isEditingPauseBinome}
                                hasDifference={hasDifference}
                                pauseData={pauseData}
                                engineTimesDebut={engineTimesDebut}
                                engineTimesFin={engineTimesFin}
                                binomeEngineTimesDebut={binomeEngineTimesDebut}
                                binomeEngineTimesFin={binomeEngineTimesFin}
                                isUpdatingPanier={isUpdatingPanier}
                                handleEditClick={handleEditClick}
                                handleSave={handleSave}
                                handleCancel={handleCancel}
                                handlePanierRepasToggle={handlePanierRepasToggle}
                                handleCreateBinomeFdt={handleCreateBinomeFdt}
                                isLoading={isLoading}
                                renderLaveuseTimes={renderLaveuseTimes}
                                helper={helper}
                                posteOperateurId={posteOperateurId}
                                setHeureDebutValue={setHeureDebutValue}
                                setHeureFinValue={setHeureFinValue}
                                setPauseValue={setPauseValue}
                                setHeureDebutBinome={setHeureDebutBinome}
                                setHeureFinBinome={setHeureFinBinome}
                                setPauseBinome={setPauseBinome}
                            />
                        </Box>

                        {/* Alertes RH */}
                        {data?.alertes?.length > 0 && (
                            <Box>
                                <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                                    <Typography variant="overline" fontWeight={700}>⚠ Alertes RH</Typography>
                                    <Chip
                                        label={pendingCount > 0 ? `${pendingCount} en attente` : "Toutes traitées ✓"}
                                        size="small"
                                        sx={{
                                            background: pendingCount > 0 ? "#fef2f2" : "#f0fdf4",
                                            color: pendingCount > 0 ? "#ef4444" : "#16a34a",
                                        }}
                                    />
                                </Stack>
                                <Stack spacing={1}>
                                    {data.alertes.map(alerte => (
                                        <EquipageAlerteRHCard
                                            key={alerte.id}
                                            alerte={alerte}
                                            posteOperateurId={posteOperateurId}
                                            dispatch={dispatch}
                                        />
                                    ))}
                                </Stack>
                            </Box>
                        )}

                        {/* Timeline véhicule */}
                        {(data?.laveusePrisePoste || data?.reporting) && (
                            <EquipageVehicleTimeline data={data} />
                        )}

                        {/* Stats tournée + temps de travail */}
                        <Box>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, color: "#6b7280", textTransform: "uppercase", letterSpacing: "0.05em", mb: 1 }}>
                                Statistiques
                            </Typography>
                            <Box sx={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                                {[
                                    { label: "Tournée", value: data.tourneeLibelle || data.reporting?.tourneeLibelle || data.client || "—", sub: data.client || null, color: "#374151" },
                                    { label: "Lavés", value: data.reporting?.nombreLaves ?? "—", color: "#22c55e" },
                                    { label: "Impossibles", value: data.reporting?.nombreLavageImpossible ?? "—", color: "#f59e0b" },
                                    { label: "Cadence", value: data.reporting?.ratioBacHeure != null ? `${data.reporting.ratioBacHeure} bacs/h` : "—", color: "#374151" },
                                ].map((s) => (
                                    <Box key={s.label} sx={{ flex: 1, minWidth: "90px", background: "#f8fafc", border: "1px solid #e5e7eb", borderRadius: "8px", p: "10px 14px" }}>
                                        <Typography sx={{ fontSize: "9px", color: "#6b7280", textTransform: "uppercase", fontWeight: 700, mb: "4px", letterSpacing: "0.04em" }}>{s.label}</Typography>
                                        <Typography sx={{ fontSize: "18px", fontWeight: 700, color: s.color, lineHeight: 1.2 }}>{s.value}</Typography>
                                        {s.sub && <Typography sx={{ fontSize: "10px", color: "#6b7280", mt: "2px" }}>{s.sub}</Typography>}
                                    </Box>
                                ))}
                            </Box>
                            {/* Fallback stats si pas de reporting */}
                            {!data.reporting && (
                                <OperateurTourneeStats date={data?.date} operateurId={data?.operateur?.id} />
                            )}
                        </Box>

                        {/* Bouton valider */}
                        <Box justifyContent="end" display="flex">
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={() => {
                                    setIsLoading(true);
                                    onFdtValidate([
                                        data.feuilleDeTemps?.id,
                                        data.binome?.feuilleDeTemps?.id
                                    ].filter(Boolean));
                                }}
                            >
                                {isLoading ? <CircularProgress size={24} /> : "Valider la feuille de temps"}
                            </Button>
                        </Box>
                    </>
                )}
            </DialogContent>
        </Dialog>
    );
}

const mapStateToProps = function (state) {
    return {
        data: state.heures.equipageDetail,
        loading: state.heures.equipageDetailLoading,
        error: state.heures.error
    };
};

export default connect(mapStateToProps)(EquipageDetailModal);
